import React, { useEffect, useState } from "react";
import helpers from "@/utils/helpers";
import {
  HighlightLeftText,
  HighlightLeftTextAdditional,
  HighlightLeftTextWithButton,
  ImageText,
  ImageTextTimer,
  TextHighlightRightGoButton,
} from "./Promotional";
import { Counter, Informative, Redirection } from "./Cyber";
import {
  CAMPAIGN_TYPE,
  ICON_DEFAULT_PROPS_PNG,
  ICON_DEFAULT_PROPS_WEBP,
  validSubtypesRedirect,
} from "@/constants/contentstack";
import "./styles.scss";

const renderPromotional = (subtype, props) => {
  const {
    highlight: { text: highlight_text, active_glyph, name_glyph },
    text: { main, additional },
    active_close_icon_button,
    banner_with_redirection,
    image_png,
    image_webp,
    time: { start_date, final_date },
    reset_days,
    active_counter,
  } = props;

  const { active_go_icon_button, url } = banner_with_redirection;
  const cleanHighlightText = highlight_text?.trim();
  const validHighlightText = !!cleanHighlightText;
  const cleanMainText = main?.trim();
  const validMainText = !!cleanMainText;
  const cleanAdditionalText = additional?.trim();
  const validAdditionalText = !!cleanAdditionalText;

  const isDefaultImg =
    !helpers.isValidImage(image_png) || !helpers.isValidImage(image_webp);
  const currentImagePng = isDefaultImg ? ICON_DEFAULT_PROPS_PNG : image_png;
  const currentImageWebp = isDefaultImg ? ICON_DEFAULT_PROPS_WEBP : image_webp;

  switch (subtype) {
    case "promotional_highlightLeft_text":
      if (validHighlightText && active_glyph && validMainText)
        return (
          <HighlightLeftText
            textHighlight={cleanHighlightText}
            activeGlyph={active_glyph}
            nameGlyph={name_glyph || ""}
            text={cleanMainText}
          />
        );
      break;
    case "promotional_highlightLeft_text_textAdditional":
      if (
        validHighlightText &&
        active_glyph &&
        validMainText &&
        validAdditionalText
      )
        return (
          <HighlightLeftTextAdditional
            textHighlight={cleanHighlightText}
            activeGlyph={active_glyph}
            nameGlyph={name_glyph || ""}
            text={cleanMainText}
            textAdditional={cleanAdditionalText}
          />
        );
      break;
    case "promotional_highlightLeft_text_closeButton":
      if (validHighlightText && validMainText && active_close_icon_button)
        return (
          <HighlightLeftTextWithButton
            textHighlight={cleanHighlightText}
            text={cleanMainText}
            activeBtnClose={active_close_icon_button}
          />
        );
      break;
    case "promotional_highlightLeft_text_goButton":
      if (
        validHighlightText &&
        validMainText &&
        active_go_icon_button &&
        helpers.isValidUrl(url)
      )
        return (
          <HighlightLeftTextWithButton
            textHighlight={cleanHighlightText}
            text={cleanMainText}
            activeBtnGo={active_go_icon_button}
            customClassBtnGo="button-go"
            customClassText="highlight-left-text-with-button__text"
          />
        );
      break;
    case "promotional_text_highlightRight_goButton":
      if (
        validHighlightText &&
        validMainText &&
        active_go_icon_button &&
        helpers.isValidUrl(url)
      )
        return (
          <TextHighlightRightGoButton
            textHighlight={cleanHighlightText}
            text={cleanMainText}
            activeBtnGo={active_go_icon_button}
          />
        );
      break;
    case "promotional_image_text":
      if (validMainText)
        return (
          <ImageText
            text={cleanMainText}
            imagePng={currentImagePng}
            imageWebp={currentImageWebp}
            isDefaultImg={isDefaultImg}
          />
        );
      break;
    case "promotional_image_text_timer":
      if (validMainText && active_counter && start_date && final_date)
        return (
          <ImageTextTimer
            text={cleanMainText}
            imagePng={currentImagePng}
            imageWebp={currentImageWebp}
            activeCounter={active_counter}
            startDate={start_date}
            finalDate={final_date}
            resetDays={reset_days}
            isDefaultImg={isDefaultImg}
          />
        );
      break;
    default:
      return null;
  }
};

const renderCyber = (subtype, props) => {
  const {
    text: { main },
    banner_with_redirection,
    image_png,
    image_webp,
    time: { start_date, final_date },
    active_counter,
    reset_days,
  } = props;
  const { active_go_icon_button, url } = banner_with_redirection;
  const cleanMainText = main?.trim();
  const validMainText = !!cleanMainText;

  switch (subtype) {
    case "cyber_informative":
      if (
        validMainText &&
        helpers.isValidImage(image_png) &&
        helpers.isValidImage(image_webp)
      )
        return (
          <Informative
            text={cleanMainText}
            imagePng={image_png}
            imageWebp={image_webp}
          />
        );
      break;
    case "cyber_redirection":
      if (
        validMainText &&
        active_go_icon_button &&
        helpers.isValidImage(image_png) &&
        helpers.isValidImage(image_webp) &&
        helpers.isValidUrl(url)
      )
        return (
          <Redirection
            text={cleanMainText}
            imagePng={image_png}
            imageWebp={image_webp}
            activeBtnGo={active_go_icon_button}
          />
        );
      break;
    case "cyber_counter":
      if (
        validMainText &&
        helpers.isValidImage(image_png) &&
        helpers.isValidImage(image_webp) &&
        active_counter &&
        start_date &&
        final_date
      )
        return (
          <Counter
            text={cleanMainText}
            imagePng={image_png}
            imageWebp={image_webp}
            activeCounter={active_counter}
            startDate={start_date}
            finalDate={final_date}
            resetDays={reset_days}
          />
        );
      break;
    default:
      return null;
  }
};

export const analytics = (nameProduct, nameCampaign, label) => {
  helpers.getTagActionName(
    nameProduct ?? "",
    "Cintillo",
    "click",
    label,
    "trackAction",
  );
  helpers.getTagMetadata(
    `Cintillo campaña${nameProduct ? ` - ${nameProduct}` : ""}`,
    nameCampaign,
    "trackMetadata",
  );
};

export const BannerCampaign = ({ dynamicContent, nameCampaign }) => {
  const content =
    dynamicContent?.find(
      ({ venta_digital_cintillo }) =>
        venta_digital_cintillo?.type === CAMPAIGN_TYPE.cyber &&
        helpers.shouldShowBanner(venta_digital_cintillo.time),
    )?.venta_digital_cintillo ||
    dynamicContent?.find(
      ({ venta_digital_cintillo }) =>
        venta_digital_cintillo?.type === CAMPAIGN_TYPE.promotional &&
        helpers.shouldShowBanner(venta_digital_cintillo.time),
    )?.venta_digital_cintillo ||
    {};

  const { time, subtype } = content;
  const hasRedirection = validSubtypesRedirect.includes(subtype);
  const validateBanner = helpers.shouldShowBanner(time);

  const [showBanner, setShowBanner] = useState(validateBanner);

  useEffect(() => {
    setShowBanner(validateBanner);
  }, [validateBanner]);

  const handleClick = (content) => {
    if (
      content?.banner_with_redirection?.active_go_icon_button &&
      helpers.isValidUrl(content?.banner_with_redirection?.url)
    ) {
      window.open(content?.banner_with_redirection?.url || "", "_blank");
    }
  };

  const renderBanner = (className, component) =>
    component && showBanner ? (
      <div
        className={className}
        onClick={() => {
          if (hasRedirection) {
            handleClick(content);
            analytics(content?.name_product ?? "", nameCampaign, "Continuar");
          }
        }}
        style={
          content?.banner_with_redirection?.active_go_icon_button &&
          helpers.isValidUrl(content?.banner_with_redirection?.url) &&
          hasRedirection
            ? { cursor: "pointer" }
            : undefined
        }
      >
        {component}
      </div>
    ) : null;

  if (content?.type === "promotional") {
    return renderBanner(
      "banner-promotional",
      renderPromotional(content?.subtype || "", content),
    );
  }

  if (content?.type === "cyber") {
    return renderBanner(
      "banner-cyberI",
      renderCyber(content?.subtype || "", content),
    );
  }

  return null;
};
